import { zodResolver } from "@hookform/resolvers/zod";
import {
	useForm,
	type Control,
	type FieldPath,
	type SubmitHandler,
} from "react-hook-form";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ReactHookForm/form";
import { getApi } from "@/hooks/useApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CardMessageType } from "@/pages/Login.page";

interface ResetPassFormProps {
	readonly setCardMessage: (message: CardMessageType) => void;
	readonly setIsResetting: (isIt: boolean) => void;
}
export function ResetPassForm(props: ResetPassFormProps) {
	const api = getApi();
	const form = useForm<FormType>({
		resolver: zodResolver(FormSchema),
	});
	const onSubmit: SubmitHandler<FormType> = async (data) => {
		props.setCardMessage(null);

		try {
			const response = await api.request("post", "/v1/Auth/rememberPassword", {
				body: {
					email: data.username,
				},
			});

			if (response.error) {
				throw response.error;
			}

			props.setCardMessage({
				msg: "Si todo ha ido bien, se mandará un correo electrónico con el enlace para restablecer tu contraseña.",
				type: "info",
			});

			props.setIsResetting(false);
		} catch (err) {
			console.error(err);

			props.setCardMessage({
				msg: "Error al intentar restablecer la contraseña.",
				type: "error",
			});
		}
	};

	return (
		<Form {...form}>
			<form
				onSubmit={() => {
					void form.handleSubmit(onSubmit)();
				}}
				className="space-y-8"
			>
				<TextControl
					control={form.control}
					name="username"
					label="Dirección de correo electrónico"
					placeholder="Dirección de correo electrónico"
					autoComplete="email"
					type="email"
				/>

				<div style={{ margin: "12px 0 12px 48px" }}>
					<Button type="submit" className="float-right">
						Enviar
					</Button>
				</div>

				<div className="flex">
					<a
						onClick={() => {
							props.setCardMessage(null);
							props.setIsResetting(false);
						}}
						className="text-blue-500 hover:underline cursor-pointer"
					>
						Volver al inicio de sesión
					</a>
				</div>
			</form>
		</Form>
	);
}

const FormSchema = z.object({
	username: z
		.email("Introduce un correo electrónico válido.")
		.transform((email) => email.toLowerCase()),
});
type FormType = z.infer<typeof FormSchema>;

function TextControl(props: {
	readonly name: FieldPath<Omit<FormType, "rememberMe">>;
	readonly control: Control<FormType>;
	readonly placeholder: string;
	readonly label: string;
	readonly autoComplete: string;
	readonly type: "text" | "email" | "password" | "tel";
}) {
	return (
		<FormField
			control={props.control}
			name={props.name}
			render={({ field }) => (
				<FormItem>
					<FormLabel>{props.label}</FormLabel>
					<FormControl>
						<Input
							placeholder={props.placeholder}
							autoComplete={props.autoComplete}
							{...field}
							onBlur={(event) => {
								if (props.type === "tel") {
									// Remove spaces and dashes
									event.target.value = event.target.value.replace(/[ -]/g, "");
									field.onChange(event);
								}
							}}
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}
